import React, { useState, useEffect } from 'react';
import { Alert , Card , Button, Modal, Radio, InputNumber, Tooltip } from 'antd';
import firebase from 'firebase/app';

import { TransferModel } from '../../models/TransferModel';

import { isLocalHost, ccy } from './AccountProfileComponents';
import { useClientTeams } from './useClientTeams';

const setDefaultTeam = (teams, currency) => {
  for (const [i, team] of teams.entries()) {
    if (team.defaultCurrency === currency) {
      return i;
    }
  }

  return 0;
}

export const CashOutToTeamDialog = ({ visible, handleOk, handleCancel, clientId, accountId, currency, clientCurrencyStr, balance, locStrings, ...rest }) => {
  const membershipTeams = useClientTeams(clientId);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTeamIndex, setSelectedTeamIndex] = useState(0);
  const [payoutAmount, setPayoutAmount] = useState(0);
  const [withdrawalError, setWithdrawalError] = useState(false);
  const [withdrawalErrorMessage, setWithdrawalErrorMessage] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  
  // Reset state when modal opens/closes
  useEffect(() => {
    if (visible) {
      // Reset all state when modal opens
      setPayoutAmount(balance ? balance / 100 : 0);
      setWithdrawalError(false);
      setWithdrawalErrorMessage("");
      setIsProcessing(false);
      setSuccessMessage("");
      setSelectedTeamIndex(0);
    }
  }, [visible, balance]);
  
  // Fetch full team details including defaultCurrency
  useEffect(() => {
    const fetchTeamDetails = async () => {
      if (!membershipTeams || membershipTeams.length === 0) {
        setTeams([]);
        setLoading(false);
        return;
      }
      
      setLoading(true);
      try {
        const db = firebase.firestore();
        const teamPromises = membershipTeams.map(async (membership) => {
          try {
            const teamDoc = await db.collection('teams').doc(membership.teamId).get();
            if (!teamDoc.exists) {
              console.warn(`Team ${membership.teamId} not found`);
              return null;
            }
            const teamData = teamDoc.data();
            return {
              ...membership,
              ...teamData,
              teamId: membership.teamId
            };
          } catch (error) {
            console.error(`Error fetching team ${membership.teamId}:`, error);
            return null;
          }
        });
        
        const fullTeams = await Promise.all(teamPromises);
        // Filter out null values (failed fetches) and filter by currency
        const validTeams = fullTeams.filter(team => team !== null);
        const filteredTeams = validTeams.filter(team => team.defaultCurrency === clientCurrencyStr);
        setTeams(filteredTeams);
        setSelectedTeamIndex(setDefaultTeam(filteredTeams, clientCurrencyStr));
      } catch (error) {
        console.error('Error fetching team details:', error);
        setTeams([]);
      } finally {
        setLoading(false);
      }
    };
    
    if (visible) {
      fetchTeamDetails();
    }
  }, [membershipTeams, clientCurrencyStr, visible]);

  const minimumWithdrawalAmount = 1;
  const minimumWithdrawalAmountString = (minimumWithdrawalAmount / 100).toFixed(2);

  const radioStyle = {
    display: 'block',
    height: '30px',
    lineHeight: '30px',
    textAlign: 'left',
  };

  const onSelectedTeamChange = (event) => {
    const index = event.target.value;
    setSelectedTeamIndex(index);    
  };

  const onPayoutAmountChange = (number) => {
    setPayoutAmount(number);
  };

  const withdrawalAvailable = () => {
    return !!teams;
  };

  const teamOptions = teams ? teams.map((team, i) => (
    <Radio 
      style={radioStyle} value={i} key={i} disabled={false}
    >
      {team.teamName}
    </Radio>
    )
  ) : <></>;

  const setWithdrawalObject = async () => {
    if (!withdrawalAvailable()) {
      handleCancel();
      return;
    }

    const isTest = isLocalHost;
    const payoutAmountCents = Math.round((payoutAmount || 0) * 100);
    
    if (payoutAmountCents <= 0) {
      setWithdrawalError(true);
      setWithdrawalErrorMessage(`Error: Please enter a valid amount greater than 0`);
      setIsProcessing(false);
      return;
    }
    
    const teamMemberDetail = teams[selectedTeamIndex];
    const teamId = teamMemberDetail.teamId;

    const params = {
      amount: +payoutAmountCents,
      currency: clientCurrencyStr,
      teamId,
      clientId,
      client: teamMemberDetail,
      accountId,
      minimumWithdrawalAmount,
      isTest,
    };

    // { amount, currency, teamId, clientId, client, accountId, minimumWithdrawalAmount, isTest }

    setIsProcessing(true);
    setWithdrawalError(false);
    setWithdrawalErrorMessage("");
    setSuccessMessage("");

    const transferModel = new TransferModel();
    const errorMessage = await transferModel.createClientToTeamTransfer(params);

    setIsProcessing(false);

    switch (errorMessage) {
      case 'Error fetching team details':
        setWithdrawalError(true);
        setWithdrawalErrorMessage('Error: Team details not found');
        return;
      case 'Error fetching client details':
        setWithdrawalError(true);
        setWithdrawalErrorMessage('Error: Client details not found');
        return;
      case 'Error fetching account details':
        setWithdrawalError(true);
        setWithdrawalErrorMessage('Error: Account details not found');
        return;
      case 'Error currency does not match':
        setWithdrawalError(true);
        setWithdrawalErrorMessage(`Error: Bank account currency does not match funds being withdrawn (${currency}). Select another payout method or add a new one in Setup Payouts`);
        return;
      case 'Error minimum withdrawal amount not met':
        setWithdrawalError(true);
        setWithdrawalErrorMessage(`Error: Minimum withdrawal is ${currency}${minimumWithdrawalAmountString}`);
        return;
      default:
        // Success case
        setWithdrawalError(false);
        setWithdrawalErrorMessage("");
        setSuccessMessage(`✅ Transfer to ${teamMemberDetail.teamName} completed successfully!`);
        // Close modal after showing success message
        setTimeout(() => {
          handleOk();
        }, 2000);
        break;
    }
  };

  // Generate unique key for modal to force reset when accountId or balance changes
  const modalKey = `transfer-to-team-${accountId}-${balance}-${visible}`;

  return (
    <Modal 
      key={modalKey}
      title="Transfer to a Team" 
      visible={visible} 
      onOk={setWithdrawalObject} 
      onCancel={handleCancel}
      confirmLoading={isProcessing}
      okText={isProcessing ? "Processing..." : "Transfer"}
      okButtonProps={{ disabled: isProcessing || loading }}
      cancelButtonProps={{ disabled: isProcessing }}
    >
      <div style={{ textAlign: 'center' }}>
        {withdrawalError && <Alert message={withdrawalErrorMessage} type="error" showIcon style={{ marginBottom: 16 }} />}
        {successMessage && <Alert message={successMessage} type="success" showIcon style={{ marginBottom: 16 }} />}

        {withdrawalAvailable()
          ? (
            <>
              <p>{`Note: no limits on Team Transfers`}</p>
              <Card>
                <p>Select Team</p>

                {teams && teams.length > 0
                  ?
                  <Radio.Group 
                    onChange={onSelectedTeamChange} 
                    value={selectedTeamIndex}
                    disabled={isProcessing || loading}
                  >
                    {teamOptions}
                  </Radio.Group>
                  :
                  <>
                    <p>You have no teams with {clientCurrencyStr} currency yet</p>
                  </>}
              </Card>

              <Card>
                <p>Enter Amount</p>
                <p>Your balance is {currency}{(balance / 100).toFixed(2)}</p>

                {balance >= minimumWithdrawalAmount
                  ?
                  <InputNumber
                    value={payoutAmount}
                    min={minimumWithdrawalAmount / 100}
                    max={balance / 100}
                    precision={2}
                    onChange={onPayoutAmountChange}
                    disabled={isProcessing || loading}
                  />
                  :
                  <p>{`Minimum payout amount is ${currency}${minimumWithdrawalAmountString}`}</p>}
              </Card>
            </>
          )
          : (
            <Card>
              <p>{`No teams with ${clientCurrencyStr} currency found!`}</p>
            </Card>
          )}
      </div>
    </Modal>
  );
};
